import {
  Children,
  PropsWithChildren,
  useEffect,
  useMemo,
  useRef,
  useState,
  FC,
  RefObject,
  ReactElement,
  isValidElement,
  createRef,
} from 'react';
import styled from 'styled-components';
import { useContainerDimensions, usePrevious } from '../hooks';
import { Box, IconButton, Stack } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';

const Wrapper = styled(Stack)`
  flex-direction: row;
  position: relative;
  gap: ${({ theme }) => theme.spacing(1)};
  overflow: hidden;
  width: 100%;
  scroll-padding-left: ${({ theme }) => theme.spacing(4)};
  scroll-padding-right: ${({ theme }) => theme.spacing(4)};
`;

const ActionButton = styled(IconButton)`
  position: absolute;
  align-self: center;
  z-index: 1;
  top: 50%;
  transform: translateY(-50%);
  background: ${({ theme }) => theme.palette.background.default};

  &:hover {
    background: ${({ theme }) => theme.palette.background.default};
  }
`;

const NextButton = styled(ActionButton)`
  left: -${({ theme }) => theme.spacing(1)};
`;

const PrevButton = styled(ActionButton)`
  right: -${({ theme }) => theme.spacing(1)};
`;

const leftGradient = 'linear-gradient(90deg, white  0%, transparent 100%)';
const rightGradient = 'linear-gradient(90deg, transparent 0%, white 100%)';

const LinearGradientMask = styled.div<{ position: 'left' | 'right' }>`
  z-index: 1;
  position: absolute;
  width: ${({ theme }) => theme.spacing(7)};
  height: 100%;
  background: ${({ position }) =>
    position === 'right' ? rightGradient : leftGradient};
  pointer-events: none;
  top: 0;
  ${({ position }) => (position === 'left' ? 'left: 0;' : 'right: 0;')}
`;

const Carousel: FC<PropsWithChildren> = ({ children }) => {
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const ref = useRef<HTMLDivElement>(null);
  const { width: containerWidth } = useContainerDimensions(ref);
  const [visibleItemsCount, setVisibleItemsCount] = useState<number>(0);
  const [itemRefs, setItemRefs] = useState<RefObject<HTMLDivElement>[]>([]);

  const items = useMemo(
    () =>
      Children.toArray(children)
        .filter((item) => isValidElement(item))
        .map((item) => item as ReactElement),
    [children]
  );

  const prevItems = usePrevious(items);

  useEffect(() => {
    setItemRefs(items.map(() => createRef()));
    //if the first item has changed, autoscroll to the first item
    if (prevItems?.length && items?.[0]?.key !== prevItems?.[0]?.key) {
      setCurrentIndex(0);
    }
  }, [items]);

  useEffect(() => {
    const carouselContainer = ref.current;
    const endOfCarousel = currentIndex + visibleItemsCount >= items.length;

    if (carouselContainer && endOfCarousel) {
      carouselContainer.scrollTo({
        left: carouselContainer.scrollWidth,
        behavior: 'smooth',
      });
    } else {
      itemRefs[currentIndex]?.current?.scrollIntoView({
        behavior: 'smooth',
        inline: 'start',
      });
    }
  }, [currentIndex]);

  useEffect(() => {
    const calculateVisibleItemsCount = () => {
      let count = 0;
      let totalWidth = 0;
      itemRefs.forEach((itemRef) => {
        const itemWidth = itemRef?.current?.offsetWidth || 0;
        totalWidth += itemWidth;
        if (totalWidth < containerWidth) {
          count++;
        }
      }, 0);
      return count;
    };

    const handleResize = () => {
      setVisibleItemsCount(calculateVisibleItemsCount());
    };
    setVisibleItemsCount(calculateVisibleItemsCount());
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [itemRefs, containerWidth]);

  const handleNext = () => {
    setCurrentIndex(Math.max(currentIndex - visibleItemsCount, 0));
  };

  const handlePrev = () => {
    setCurrentIndex(
      currentIndex +
        Math.min(
          visibleItemsCount,
          items.length - visibleItemsCount - currentIndex
        )
    );
  };

  return (
    <Box sx={{ width: '100%', position: 'relative' }}>
      {currentIndex > 0 && (
        <>
          <LinearGradientMask position="left" />
          <NextButton size="small" onClick={handleNext}>
            <ChevronLeft />
          </NextButton>
        </>
      )}
      <Wrapper ref={ref}>
        {items.map((item, index) => (
          <div ref={itemRefs[index]} key={index}>
            {item}
          </div>
        ))}
      </Wrapper>
      {currentIndex + visibleItemsCount < items.length && (
        <>
          <LinearGradientMask position="right" />
          <PrevButton size="small" onClick={handlePrev}>
            <ChevronRight />
          </PrevButton>
        </>
      )}
    </Box>
  );
};

export default Carousel;
